// src/components/quiz/QuestionMatch.jsx
import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';

export default function QuestionMatch({ 
  matchData, 
  matchState, 
  setMatchState, 
  currentQ, 
  feedback, 
  onComplete 
}) {
  const [justMatched, setJustMatched] = useState([]);
  const totalPairs = currentQ.pairs.length;
  const completedPairs = matchState.matched.length / 2;

  // Limpiar animación de "justMatched" después de mostrarla
  useEffect(() => {
    if (justMatched.length > 0) {
      const timer = setTimeout(() => setJustMatched([]), 600);
      return () => clearTimeout(timer);
    }
  }, [justMatched]);

  const handleMatchCardClick = (card) => {
    if (feedback || matchState.isFinishing) return;
    if (matchState.matched.includes(card.id)) return;

    // ✅ MEJORA 1: Si haces click en la misma tarjeta, deseleccionar
    if (matchState.selected?.id === card.id) {
      setMatchState(prev => ({ ...prev, selected: null }));
      return;
    }

    // ✅ MEJORA 2: Si ya hay una selección del MISMO lado, reemplazarla automáticamente
    if (matchState.selected && matchState.selected.col === card.col) {
      setMatchState(prev => ({ ...prev, selected: card, wrong: false }));
      return;
    }

    // Si no hay nada seleccionado, seleccionar esta tarjeta
    if (!matchState.selected) {
      setMatchState(prev => ({ ...prev, selected: card }));
      return;
    }

    // ✅ VALIDACIÓN DEL EMPAREJAMIENTO
    const leftText = matchState.selected.col === 'left' ? matchState.selected.text : card.text;
    const rightText = matchState.selected.col === 'right' ? matchState.selected.text : card.text;

    const isValid = currentQ.pairs.some(p => 
      p.left.trim().toLowerCase() === leftText.trim().toLowerCase() && 
      p.right.trim().toLowerCase() === rightText.trim().toLowerCase()
    );

    if (isValid) {
      // ✅ MATCH CORRECTO
      const newMatched = [...matchState.matched, matchState.selected.id, card.id];
      const isDone = newMatched.length === (totalPairs * 2);

      // Guardar IDs recién emparejados para animación
      setJustMatched([matchState.selected.id, card.id]);

      setMatchState({ 
        selected: null, 
        matched: newMatched, 
        wrong: false, 
        isFinishing: isDone 
      });

      if (isDone) {
        setTimeout(() => onComplete("completed"), 800);
      }
    } else {
      // ❌ MATCH INCORRECTO
      setMatchState(prev => ({ ...prev, wrong: true }));
      setTimeout(() => {
        setMatchState(prev => ({ ...prev, selected: null, wrong: false }));
      }, 500);
    }
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      {/* HEADER CON PROGRESO */}
      <div className="bg-white py-4 px-6 rounded-[2rem] border-2 border-slate-100 shadow-sm text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Sparkles size={14} className="text-indigo-400" />
          <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">
            CONECTA LAS PAREJAS
          </span>
          <Sparkles size={14} className="text-indigo-400" />
        </div>
        
        {/* ✅ CONTADOR DE PROGRESO */}
        <div className="flex items-center justify-center gap-2 mt-2">
          {[...Array(totalPairs)].map((_, i) => (
            <div
              key={i}
              className={`w-6 h-1.5 rounded-full transition-all duration-500 ${
                i < completedPairs 
                  ? 'bg-green-500 scale-110' 
                  : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
        
        <p className="text-xs font-bold text-slate-500 mt-2">
          {completedPairs} de {totalPairs} pares
        </p>
      </div>

      {/* COLUMNAS DE TARJETAS */}
      <div className="flex gap-3 md:gap-6">
        {['left', 'right'].map(side => (
          <div key={side} className="flex-1 space-y-3">
            {matchData[side].map((item) => {
              const isMatched = matchState.matched.includes(item.id);
              const isSelected = matchState.selected?.id === item.id;
              const isJustMatched = justMatched.includes(item.id);
              const isWrongSelection = isSelected && matchState.wrong;

              return (
                <button
                  key={item.id}
                  disabled={isMatched}
                  onClick={() => handleMatchCardClick(item)}
                  className={`
                    w-full py-4 px-3 rounded-2xl font-bold text-sm border-b-4 
                    transition-all duration-300 min-h-[3.5rem] relative overflow-hidden
                    ${isMatched
                      ? 'opacity-0 scale-75 pointer-events-none'
                      : isJustMatched
                        ? 'bg-green-500 border-green-700 text-white scale-105 animate-bounce'
                        : isWrongSelection
                          ? 'bg-red-500 border-red-700 text-white animate-shake'
                          : isSelected
                            ? 'bg-indigo-600 border-indigo-900 text-white scale-105 shadow-xl ring-4 ring-indigo-200'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-300 hover:scale-102 active:scale-95 shadow-sm'
                    }
                  `}
                >
                  {/* ✅ EFECTO DE BRILLO EN SELECCIONADAS */}
                  {isSelected && !isWrongSelection && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                  )}
                  
                  {item.text}

                  {/* ✅ CHECKMARK EN MATCHES EXITOSOS */}
                  {isJustMatched && (
                    <span className="absolute top-1 right-1 text-white text-xs">✓</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* ✅ MENSAJE DE AYUDA CONTEXTUAL */}
      {matchState.selected && (
        <div className="text-center animate-in fade-in slide-in-from-bottom-2 duration-300">
          <p className="text-xs font-bold text-indigo-600 bg-indigo-50 py-2 px-4 rounded-xl inline-block">
            Selecciona la pareja correcta de la columna {matchState.selected.col === 'left' ? 'derecha' : 'izquierda'} 💡
          </p>
        </div>
      )}

      {/* ESTILOS ADICIONALES */}
      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-shake {
          animation: shake 0.3s ease-in-out 0s 2;
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        .hover\:scale-102:hover {
          transform: scale(1.02);
        }
      `}</style>
    </div>
  );
}
